import { useParams, useLocation } from "react-router-dom";
import { useContext, useEffect, useState, useCallback } from "react";
import ProductApi from "../../api/product";
import ProductTypeApi from "../../api/product-type";
import PurchaseTicketApi from "../../api/purchase-ticket";
import CounterProductApi from "../../api/counter-product";
import { getCopyOf, localizeNumbers } from "../../functions";
import ShoppingCardWarning from "../../pages/purchase/warning";
import ProductTypeSpecs, {
  ProductTypeSpec,
  SpecDisplay,
} from "../../types/prtoduct-type-specs";
import AuthContext from "../auth-context";
import SearchFilters from "../search-filters";
import FilterWrapper from "../search-filters/filter-wrapper";
import SortBox from "../sort-box";
import Col from "../tailwind/col";
import Row from "../tailwind/row";
import ProductItem from "./item";
import ProductSuggest from "./suggest";
import Product from "../../types/product";
import useAppNavigate from "../router/use-app-navigate";
import Loading from "../layout/loading";

export interface IProductListProps {
  onSuccess: (ticketId: string) => void;
  isShop: Boolean;
  ngnNumber?: string;
  type?: number;
  shippingId?: number;
  tags?: string;
}

export default function ProductList({
  onSuccess,
  isShop,
  ngnNumber,
  type,
  tags,
  shippingId: initialShippingId,
}: IProductListProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [conterProducts, setConterProducts] = useState<Product[]>([]);
  const [productSpecs, setProductSpecs] = useState<ProductTypeSpecs>({});
  const [sortBy, setSortBy] = useState<string>("newest");
  const [showCounter, setShowCounter] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [mainModemProduct, setMainModemProduct] = useState<Product>();
  const [modemProduct, setModemProduct] = useState<Product>();
  const [hasModem, setHasModem] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [shouldFinalize, setShouldFinalize] = useState(false);
  const [snappPayOnly, setSnappPayOnly] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedShippingId, setSelectedShippingId] = useState<
    number | null | undefined
  >(initialShippingId ?? null);

  const context = useContext(AuthContext);
  const { activeFileId } = context;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedType = isShop ? type : queryParams.get("type");

  let productType = 0;
  const { type: typeParam } = useParams();
  if (!isShop) {
    switch (typeParam) {
      case "service":
        productType = 0;
        break;
      case "packages":
        productType = 4;
        break;
      case "ip":
        productType = 6;
        break;
      default:
        productType = 0;
    }
  }

  let medium = 0;
  if (isShop) {
    switch (Number(type)) {
      case 0:
        medium = 0;
        break;
      case 1:
        medium = 30;
        break;
      case 2:
        medium = 40;
        break;
      case 3:
        medium = 31;
        break;
    }
  }

  let navigate = useAppNavigate();

  const onFinalSelected = useCallback(
    (productIdentifiers: number[], shippingIdentifier?: number | null) => {
      if (isShop) {
        navigate("/shop/cellPhone", {
          state: {
            type: type!,
            tariffId: hasModem
              ? (mainModemProduct?.identifier ?? 1)
              : productIdentifiers[0],
            ngnNumber,
            optionalProducts: hasModem
              ? productIdentifiers
              : productIdentifiers.slice(1),
            shippingId: shippingIdentifier,
            tags: tags,
          },
        });
      } else {
        // console.log("context", context);
        new PurchaseTicketApi(context)
          .post(
            activeFileId!,
            productType,
            hasModem
              ? (mainModemProduct?.identifier ?? 1)
              : productIdentifiers[0],
            hasModem ? productIdentifiers : productIdentifiers.slice(1),
            shippingIdentifier ?? undefined,
          )
          .then((ticket) => {
            if (ticket.errorMessage) {
              setErrorMessage(ticket.errorMessage);
            } else {
              setErrorMessage(null);
              new PurchaseTicketApi(context)
                .getAll()
                .then(context.setTickets)
                .catch(() => {});
              onSuccess(ticket.id);
            }
          })
          .catch(console.error);
      }
    },
    [
      context,
      activeFileId,
      productType,
      hasModem,
      mainModemProduct?.identifier,
      onSuccess,
      isShop,
      type,
      ngnNumber,
      navigate,
      selectedShippingId,
    ]
  );

  useEffect(() => {
    if (shouldFinalize && !showCounter && selectedProducts.length > 0) {
      if (!hasModem && selectedProducts.length > 1) {
        const lastSelected = selectedProducts[selectedProducts.length - 1];
        setSelectedProducts([lastSelected]);
        setShouldFinalize(true);
        return;
      }

      onFinalSelected(selectedProducts);
      setShouldFinalize(false);
    }
  }, [shouldFinalize, showCounter, selectedProducts, hasModem]);

  useEffect(() => {
    if (!isShop && activeFileId == null) return;

    setLoading(true);
    const productApi = new ProductApi(context);
    const productTypeApi = new ProductTypeApi(context);
    const mediumValue = isShop ? medium : undefined;
    const lineId = activeFileId ?? null;

    productApi
      .getAll(lineId, productType, lineId ? undefined : mediumValue)
      .then(setProducts);

    productTypeApi
      .get(productType, lineId, lineId ? undefined : mediumValue)
      .then((result) => result && setProductSpecs(result.specs))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeFileId, isShop, productType, medium]);

  const handleProductSelection = (productId: number, result: Product[]) => {
    const product = products.find((p) => p.id === productId);
    if (result.length !== 0) {
      if (
        product?.specs?.some(
          (spec) => spec.specId === 10 && spec.value !== null,
        )
      ) {
        setShowCounter(true);
        setModemState(result);
      } else {
        addProductToSelected(productId);
      }
      setConterProducts(result);
    } else {
      addProductToSelected(productId);
    }
  };

  const setModemState = (result: Product[]) => {
    setMainModemProduct(result[0]);
    setModemProduct(result[1]);
    setHasModem(true);
    updateSelectedProducts(result[1].identifier);
  };

  const addProductToSelected = (productId: number) => {
    setHasModem(false);
    updateSelectedProducts(productId);
  };

  const updateSelectedProducts = (productId: number) => {
    setSelectedProducts((prevSelected) => {
      if (prevSelected.includes(productId)) {
        setShouldFinalize(true);
        return prevSelected;
      }
      if (!hasModem && prevSelected.length >= 1) {
        setShouldFinalize(true);
        return [productId];
      }
      const newSelectedProducts = [...prevSelected, productId];
      setShouldFinalize(true);
      return newSelectedProducts;
    });
  };

  const productSelected = (productId: number): void => {
    new CounterProductApi(context)
      .get(productId)
      .then((result) => handleProductSelection(productId, result))
      .catch(console.error);
  };

  const selectedValuesChanges = (specId: string, data: string[]) => {
    const specs = getCopyOf(productSpecs);
    specs[specId].selectedValues = data ? [...data] : null;
    setProductSpecs(specs);
  };

  const selectedRangeChanges = (specId: string, data: number[]) => {
    const specs = getCopyOf(productSpecs);
    specs[specId].selectedRange = data ? [...data] : null;
    setProductSpecs(specs);
  };

  const isInRangeOfSpec = (spec: ProductTypeSpec, value: number) => {
    const { selectedRange } = spec || {};
    return selectedRange
      ? selectedRange[0] <= value && selectedRange[1] >= value
      : true;
  };

  const getSortedFilteredProducts = () => {
    if (!products) return [];
    return sortProducts(filterProducts(products));
  };

  const sortProducts = (products: Product[]) => {
    return products.sort(compareProducts);
  };

  const compareProducts = (a: Product, b: Product) => {
    switch (sortBy) {
      case "newest":
        return b.id - a.id;
      case "grandest":
        return b.finalPrice - a.finalPrice;
      case "cheapest":
        return a.finalPrice - b.finalPrice;
      default:
        return 0;
    }
  };

  const filterProducts = (products: Product[]) => {
    return products.filter((product) => {
      if (snappPayOnly && !product.hasSnappPay) return false;
      if (!isInRangeOfSpec(productSpecs[255], product.finalPrice)) return false;
      return product.specs.every((spec) => {
        if (!(spec.specId in productSpecs)) return false;
        const { selectedValues } = productSpecs[spec.specId];
        switch (productSpecs[spec.specId].display) {
          case SpecDisplay.CheckBox:
          case SpecDisplay.DropDown:
          case SpecDisplay.RadioButton:
            return (
              !selectedValues?.length ||
              selectedValues.includes(`${spec.value ?? ""}`)
            );
          case SpecDisplay.Search:
            return (
              !selectedValues?.length ||
              localizeNumbers(`${spec.value ?? ""}`).includes(
                localizeNumbers(selectedValues[0])
              )
            );
          case SpecDisplay.Slider:
            return isInRangeOfSpec(productSpecs[spec.specId], spec.value);
          default:
            return true;
        }
      });
    });
  };

  const handleSort = (value: string) => setSortBy(value);

  const specs = Object.keys(productSpecs).map((specId) => (
    <FilterWrapper
      key={specId}
      spec={productSpecs[specId]}
      selectedRangeChanges={(r) => selectedRangeChanges(specId, r)}
      selectedValuesChanges={(v) => selectedValuesChanges(specId, v)}
    />
  ));

  const mainProduct = conterProducts.find((p) => p.counterProductType === 0);
  const modem = conterProducts.find((p) => p.counterProductType === 1);
  const relatedProducts = conterProducts.filter(
    (p) => p.counterProductType === 2,
  );
  const shippingProducts = conterProducts.filter(
    (p) => p.counterProductType === 3,
  );

  return (
    <>
      <ShoppingCardWarning />
      <Row>
        <SearchFilters className="col-12 col-lg-3">{specs}</SearchFilters>

        <Col className="col-lg-9 pb-3">
          <SortBox onChange={handleSort} value={sortBy} />
          {errorMessage && (
            <div className="text-red-500 text-right p-3 mb-4">
              {errorMessage}
            </div>
          )}

          <Row className="row d-flex justify-content-center mx-1 mx-lg-0">
            {loading ? (
              <Loading text={"درحال بارگذاری محصولات"}></Loading>
            ) : (
              getSortedFilteredProducts().map((product) => (
                <ProductItem
                  key={product.id}
                  title={product.title}
                  finalPrice={product.finalPrice}
                  mainPrice={product.mainPrice}
                  packageTitle={product.packageTitle}
                  desc={product.desc}
                  Specs={productSpecs}
                  productSpecs={product.specs}
                  hasSnappPay={product.hasSnappPay}
                  onSelect={() => productSelected(product.id)}
                  productType={productType}
                />
              ))
            )}

            <ProductSuggest
              show={showCounter}
              mainProducts={mainProduct ? [mainProduct] : []}
              optionalProducts={modem ? [modem] : []}
              relatedProducts={relatedProducts}
              shippingProducts={shippingProducts}
              selectedShippingId={selectedShippingId}
              onSelectShipping={setSelectedShippingId}
              onCancel={() => setShowCounter(false)}
              onSubmit={(products, shippingId) =>
                onFinalSelected(products, shippingId)
              }
            />
          </Row>
        </Col>
      </Row>
    </>
  );
}
